import { useState, useMemo, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import ERPLayout from "@/components/feature/ERPLayout";
import { mockLeaveRequests } from "@/mocks/leaves";
import { mockPublicHolidays } from "@/mocks/leaves";
import { mockJobAssignmentHistory } from "@/mocks/employees";
import { employeesApi } from "@/services/api";

// ─── helpers ────────────────────────────────────────────────────────────────
function toYMD(d: Date) { return d.toISOString().split("T")[0]; }
function addDays(d: Date, n: number) { const r = new Date(d); r.setDate(r.getDate() + n); return r; }
function dateRange(from: string, to: string): string[] {
  const dates: string[] = [];
  const cur = new Date(from + "T00:00:00");
  const end = new Date(to + "T00:00:00");
  while (cur <= end) { dates.push(toYMD(cur)); cur.setDate(cur.getDate() + 1); }
  return dates;
}
function formatShort(d: Date) { return d.toLocaleDateString("en-IN", { day: "numeric", month: "short" }); }
function getDayLabel(d: Date) { return d.toLocaleDateString("en-IN", { weekday: "short" }); }

const DEPT_COLORS: Record<string, { bg: string; text: string; bar: string }> = {
  "Field Operations": { bg: "#fef3c7", text: "#92400e", bar: "#d97706" },
  "Office":           { bg: "#fffbeb", text: "#b45309", bar: "#f59e0b" },
  "Management":       { bg: "#fef9c3", text: "#854d0e", bar: "#d97706" },
  "Lab":              { bg: "#fef3c7", text: "#78350f", bar: "#b45309" },
};

type AvailStatus = "Available" | "On Leave" | "Holiday" | "Weekend" | "Assigned";

function getCellStatus(dateStr: string, empId: number, leaveDates: Set<string>, holidayDates: Set<string>, assignedDates: Set<string>): AvailStatus {
  const dow = new Date(dateStr + "T00:00:00").getDay();
  if (dow === 0) return "Weekend";
  if (holidayDates.has(dateStr)) return "Holiday";
  if (leaveDates.has(dateStr)) return "On Leave";
  if (assignedDates.has(dateStr)) return "Assigned";
  return "Available";
}

const STATUS_CELL: Record<AvailStatus, { bg: string; title: string }> = {
  "Available":  { bg: "#dcfce7", title: "Available" },
  "On Leave":   { bg: "#fecaca", title: "On Leave" },
  "Holiday":    { bg: "#e9d5ff", title: "Public Holiday" },
  "Weekend":    { bg: "#f3f4f6", title: "Weekend" },
  "Assigned":   { bg: "#fef9c3", title: "Assigned to Job" },
};

interface StaffMember {
  id: string | number;
  name: string;
  department: string;
  role: string;
  avatar: string;
  status: string;
}

// ─── main page ───────────────────────────────────────────────────────────────
export default function TeamAvailabilityPage() {
  const navigate = useNavigate();
  const today = new Date();

  const [rangeStart, setRangeStart] = useState(toYMD(today));
  const [daysToShow, setDaysToShow] = useState(14);
  const [deptFilter, setDeptFilter] = useState("All");
  const [viewMode, setViewMode] = useState<"grid" | "heatmap">("grid");
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [planForm, setPlanForm] = useState({ jobTitle: "", from: "", to: "", dept: "Field Operations", minAvailable: "2" });
  const [planResult, setPlanResult] = useState<{ date: string; available: string[] }[] | null>(null);
  const [allStaff, setAllStaff] = useState<StaffMember[]>([]);
  const [staffLoading, setStaffLoading] = useState(true);

  const departments = ["All", "Field Operations", "Office", "Management", "Lab"];

  // Load real staff from API
  useEffect(() => {
    const fetchStaff = async () => {
      setStaffLoading(true);
      try {
        const res = await employeesApi.getAll() as { data?: StaffMember[] } | StaffMember[];
        const raw = Array.isArray(res) ? res : (res as { data?: StaffMember[] }).data || [];
        const mapped: StaffMember[] = (raw as Record<string, unknown>[]).map((e) => ({
          id: String(e.id || ""),
          name: String(e.name || ""),
          department: String(e.department || "Office"),
          role: String(e.role || e.designation || ""),
          avatar: String(e.avatar || (e.name as string || "??").split(" ").map((w: string) => w[0]).join("").slice(0, 2).toUpperCase()),
          status: String(e.status || "active"),
        })).filter((e) => e.status === "active" || e.status === "Active");
        setAllStaff(mapped);
      } catch {
        // fallback: empty list
        setAllStaff([]);
      } finally {
        setStaffLoading(false);
      }
    };
    fetchStaff();
  }, []);

  // Build date columns
  const dateColumns = useMemo(() => {
    const cols: Date[] = [];
    const start = new Date(rangeStart + "T00:00:00");
    for (let i = 0; i < daysToShow; i++) cols.push(addDays(start, i));
    return cols;
  }, [rangeStart, daysToShow]);

  // Build leave date sets per employee (using userId string key)
  const empLeaveDates = useMemo(() => {
    const map: Record<string, Set<string>> = {};
    mockLeaveRequests.filter(l => l.status === "Approved").forEach(l => {
      const key = String(l.empId);
      if (!map[key]) map[key] = new Set();
      dateRange(l.from, l.to).forEach(d => map[key].add(d));
    });
    return map;
  }, []);

  // Build assigned dates per employee (from job history)
  const empAssignedDates = useMemo(() => {
    const map: Record<string, Set<string>> = {};
    mockJobAssignmentHistory.filter(j => j.status === "In Progress").forEach(j => {
      const key = String((j as Record<string, unknown>).userId || (j as Record<string, unknown>).empId || "");
      if (!map[key]) map[key] = new Set();
      dateRange(j.from, j.to).forEach(d => map[key].add(d));
    });
    return map;
  }, []);

  // Holiday set
  const holidayDates = useMemo(() => {
    const s = new Set<string>();
    mockPublicHolidays.forEach(h => s.add(h.date));
    return s;
  }, []);

  // Filtered employees
  const filteredEmps = useMemo(() => {
    return allStaff.filter(e => deptFilter === "All" || e.department === deptFilter);
  }, [deptFilter, allStaff]);

  // Availability score per employee (% available days in range)
  const availScore = useMemo(() => {
    const scores: Record<string, number> = {};
    filteredEmps.forEach(emp => {
      const key = String(emp.id);
      const leaveDates = empLeaveDates[key] || new Set<string>();
      const assignedDates = empAssignedDates[key] || new Set<string>();
      let avail = 0; let total = 0;
      dateColumns.forEach(d => {
        const ds = toYMD(d);
        const dow = d.getDay();
        if (dow === 0 || dow === 6) return;
        total++;
        const s = getCellStatus(ds, 0, leaveDates, holidayDates, assignedDates);
        if (s === "Available") avail++;
      });
      scores[key] = total > 0 ? Math.round((avail / total) * 100) : 0;
    });
    return scores;
  }, [filteredEmps, dateColumns, empLeaveDates, empAssignedDates, holidayDates]);

  // Daily team availability count
  const dailyAvailCount = useMemo(() => {
    const map: Record<string, number> = {};
    dateColumns.forEach(d => {
      const ds = toYMD(d);
      let count = 0;
      filteredEmps.forEach(emp => {
        const key = String(emp.id);
        const s = getCellStatus(ds, 0, empLeaveDates[key] || new Set(), holidayDates, empAssignedDates[key] || new Set());
        if (s === "Available") count++;
      });
      map[ds] = count;
    });
    return map;
  }, [filteredEmps, dateColumns, empLeaveDates, empAssignedDates, holidayDates]);

  // Project planning: find best dates
  const handleFindDates = () => {
    if (!planForm.from || !planForm.to) return;
    const min = parseInt(planForm.minAvailable) || 2;
    const deptEmps = allStaff.filter(e => planForm.dept === "All" || e.department === planForm.dept);
    const days = dateRange(planForm.from, planForm.to);
    const results = days.map(ds => {
      const dow = new Date(ds + "T00:00:00").getDay();
      if (dow === 0 || dow === 6) return null;
      if (holidayDates.has(ds)) return null;
      const available = deptEmps.filter(emp => {
        const key = String(emp.id);
        const s = getCellStatus(ds, 0, empLeaveDates[key] || new Set(), holidayDates, empAssignedDates[key] || new Set());
        return s === "Available";
      }).map(e => e.name);
      return available.length >= min ? { date: ds, available } : null;
    }).filter(Boolean) as { date: string; available: string[] }[];
    setPlanResult(results);
  };

  const totalAvailToday = filteredEmps.filter(emp => {
    const key = String(emp.id);
    const s = getCellStatus(toYMD(today), 0, empLeaveDates[key] || new Set(), holidayDates, empAssignedDates[key] || new Set());
    return s === "Available";
  }).length;
  const onLeaveToday = filteredEmps.filter(emp => (empLeaveDates[String(emp.id)] || new Set()).has(toYMD(today))).length;
  const assignedToday = filteredEmps.filter(emp => (empAssignedDates[String(emp.id)] || new Set()).has(toYMD(today))).length;

  return (
    <ERPLayout title="Team Availability">
      <div className="p-6 space-y-5">

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <button onClick={() => navigate("/employees")} className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 cursor-pointer mt-0.5">
              <i className="ri-arrow-left-line text-lg" />
            </button>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Team Availability Dashboard</h2>
              <p className="text-sm text-gray-500 mt-0.5">Plan projects based on real-time team availability</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/employees/leave-calendar" className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap">
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-calendar-line text-sm" /></span>
              Leave Calendar
            </Link>
            <button onClick={() => setShowPlanModal(true)} className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 cursor-pointer whitespace-nowrap">
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-search-line text-sm" /></span>
              Find Available Dates
            </button>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: "Available Today", value: totalAvailToday, icon: "ri-user-follow-line", color: "#16a34a", bg: "#ecfdf5" },
            { label: "On Leave Today", value: onLeaveToday, icon: "ri-user-unfollow-line", color: "#ef4444", bg: "#fef2f2" },
            { label: "On Assignment", value: assignedToday, icon: "ri-map-pin-user-line", color: "#d97706", bg: "#fffbeb" },
            { label: "Total Team", value: filteredEmps.length, icon: "ri-team-line", color: "#7c3aed", bg: "#f5f3ff" },
          ].map((k) => (
            <div key={k.label} className="bg-white rounded-xl border border-gray-100 p-4">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center mb-3" style={{ background: k.bg }}>
                <i className={`${k.icon} text-base`} style={{ color: k.color }} />
              </div>
              <p className="text-2xl font-bold text-gray-900">{k.value}</p>
              <p className="text-xs text-gray-500 mt-0.5">{k.label}</p>
            </div>
          ))}
        </div>

        {/* Controls */}
        <div className="bg-white rounded-xl border border-gray-100 p-4 flex flex-col sm:flex-row gap-3 flex-wrap items-center">
          <div className="flex items-center gap-2">
            <label className="text-xs font-medium text-gray-600 whitespace-nowrap">Start Date</label>
            <input type="date" value={rangeStart} onChange={e => setRangeStart(e.target.value)} className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 cursor-pointer" />
          </div>
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
            {[7, 14, 21, 30].map(n => (
              <button key={n} onClick={() => setDaysToShow(n)} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${daysToShow === n ? "bg-white text-gray-900" : "text-gray-500 hover:text-gray-700"}`}>{n}d</button>
            ))}
          </div>
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 flex-wrap">
            {departments.map(d => (
              <button key={d} onClick={() => setDeptFilter(d)} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${deptFilter === d ? "bg-white text-gray-900" : "text-gray-500 hover:text-gray-700"}`}>{d}</button>
            ))}
          </div>
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 ml-auto">
            <button onClick={() => setViewMode("grid")} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5 ${viewMode === "grid" ? "bg-white text-gray-900" : "text-gray-500"}`}>
              <i className="ri-grid-line text-sm" />Grid
            </button>
            <button onClick={() => setViewMode("heatmap")} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5 ${viewMode === "heatmap" ? "bg-white text-gray-900" : "text-gray-500"}`}>
              <i className="ri-fire-line text-sm" />Heatmap
            </button>
          </div>
        </div>

        {/* ── GRID VIEW ── */}
        {viewMode === "grid" && (
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full" style={{ minWidth: `${200 + dateColumns.length * 44}px` }}>
                <thead>
                  {/* Daily availability count row */}
                  <tr className="border-b border-gray-100 bg-gray-50/60">
                    <th className="text-left px-4 py-2 w-48 flex-shrink-0">
                      <span className="text-xs font-semibold text-gray-500">Team / Date</span>
                    </th>
                    {dateColumns.map(d => {
                      const ds = toYMD(d);
                      const count = dailyAvailCount[ds] ?? 0;
                      const total = filteredEmps.length;
                      const pct = total > 0 ? count / total : 0;
                      const dow = d.getDay();
                      const isWeekend = dow === 0 || dow === 6;
                      const isHoliday = holidayDates.has(ds);
                      const isToday = ds === toYMD(today);
                      return (
                        <th key={ds} className={`text-center px-1 py-2 w-11 ${isToday ? "bg-amber-50" : ""}`}>
                          <div className={`text-xs font-semibold ${isToday ? "text-amber-600" : isWeekend ? "text-gray-300" : isHoliday ? "text-violet-500" : "text-gray-500"}`}>
                            {getDayLabel(d)}
                          </div>
                          <div className={`text-xs font-bold ${isToday ? "text-amber-700" : isWeekend ? "text-gray-300" : "text-gray-700"}`}>
                            {d.getDate()}
                          </div>
                          {!isWeekend && !isHoliday && (
                            <div className="mt-1 mx-auto w-7 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                              <div className="h-full rounded-full transition-all" style={{ width: `${pct * 100}%`, background: pct >= 0.7 ? "#10b981" : pct >= 0.4 ? "#f59e0b" : "#ef4444" }} />
                            </div>
                          )}
                        </th>
                      );
                    })}
                    <th className="text-center px-3 py-2 w-20">
                      <span className="text-xs font-semibold text-gray-500">Avail %</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {staffLoading ? (
                    <tr><td colSpan={dateColumns.length + 2} className="text-center py-8 text-sm text-gray-400"><i className="ri-loader-4-line animate-spin mr-2" />Loading staff...</td></tr>
                  ) : filteredEmps.map(emp => {
                    const key = String(emp.id);
                    const leaveDates = empLeaveDates[key] || new Set<string>();
                    const assignedDates = empAssignedDates[key] || new Set<string>();
                    const dc = DEPT_COLORS[emp.department] || { bg: "#f3f4f6", text: "#374151", bar: "#6b7280" };
                    const score = availScore[key] ?? 0;
                    return (
                      <tr key={key} className="hover:bg-gray-50/40 transition-all">
                        <td className="px-4 py-2.5 w-48">
                          <div className="flex items-center gap-2.5">
                            <div className="w-8 h-8 rounded-full bg-amber-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">{emp.avatar}</div>
                            <div className="min-w-0">
                              <p className="text-xs font-semibold text-gray-800 truncate">{emp.name}</p>
                              <span className="text-xs px-1.5 py-0.5 rounded-full font-medium" style={{ background: dc.bg, color: dc.text }}>{emp.department.split(" ")[0]}</span>
                            </div>
                          </div>
                        </td>
                        {dateColumns.map(d => {
                          const ds = toYMD(d);
                          const status = getCellStatus(ds, 0, leaveDates, holidayDates, assignedDates);
                          const cell = STATUS_CELL[status];
                          const isToday = ds === toYMD(today);
                          return (
                            <td key={ds} className={`px-1 py-2 text-center ${isToday ? "bg-amber-50/30" : ""}`}>
                              <div
                                className="w-7 h-7 rounded-lg mx-auto flex items-center justify-center cursor-default transition-all hover:scale-110"
                                style={{ background: cell.bg }}
                                title={`${emp.name} — ${cell.title} on ${formatShort(d)}`}
                              >
                                {status === "Available" && <i className="ri-check-line text-amber-600" style={{ fontSize: "10px" }} />}
                                {status === "On Leave" && <i className="ri-user-unfollow-line text-red-400" style={{ fontSize: "10px" }} />}
                                {status === "Holiday" && <i className="ri-star-line text-violet-500" style={{ fontSize: "10px" }} />}
                                {status === "Weekend" && <span className="text-gray-300" style={{ fontSize: "9px" }}>—</span>}
                                {status === "Assigned" && <i className="ri-map-pin-line text-amber-500" style={{ fontSize: "10px" }} />}
                              </div>
                            </td>
                          );
                        })}
                        <td className="px-3 py-2 text-center">
                          <div className="flex flex-col items-center gap-1">
                            <span className={`text-xs font-bold ${score >= 70 ? "text-amber-600" : score >= 40 ? "text-amber-500" : "text-red-500"}`}>{score}%</span>
                            <div className="w-12 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                              <div className="h-full rounded-full" style={{ width: `${score}%`, background: score >= 70 ? "#10b981" : score >= 40 ? "#f59e0b" : "#ef4444" }} />
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Legend */}
            <div className="px-4 py-3 border-t border-gray-50 flex flex-wrap items-center gap-4">
              {Object.entries(STATUS_CELL).map(([status, { bg, title }]) => (
                <span key={status} className="flex items-center gap-1.5 text-xs text-gray-500">
                  <span className="w-4 h-4 rounded inline-block border border-gray-100" style={{ background: bg }} />
                  {title}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* ── HEATMAP VIEW ── */}
        {viewMode === "heatmap" && (
          <div className="space-y-4">
            {/* Daily availability bar chart */}
            <div className="bg-white rounded-xl border border-gray-100 p-5">
              <h3 className="text-sm font-semibold text-gray-800 mb-4">Daily Team Availability</h3>
              <div className="flex items-end gap-1 h-32 overflow-x-auto pb-2">
                {dateColumns.map(d => {
                  const ds = toYMD(d);
                  const count = dailyAvailCount[ds] ?? 0;
                  const total = filteredEmps.length || 1;
                  const pct = count / total;
                  const dow = d.getDay();
                  const isWeekend = dow === 0 || dow === 6;
                  const isHoliday = holidayDates.has(ds);
                  const isToday = ds === toYMD(today);
                  const barColor = isWeekend || isHoliday ? "#e5e7eb" : pct >= 0.7 ? "#10b981" : pct >= 0.4 ? "#f59e0b" : "#ef4444";
                  return (
                    <div key={ds} className="flex flex-col items-center gap-1 flex-shrink-0" style={{ width: "36px" }}>
                      <span className="text-xs font-bold text-gray-600" style={{ fontSize: "9px" }}>{isWeekend || isHoliday ? "" : count}</span>
                      <div className="w-7 rounded-t-md transition-all" style={{ height: `${isWeekend || isHoliday ? 8 : Math.max(4, pct * 96)}px`, background: barColor, opacity: isToday ? 1 : 0.8 }} title={`${formatShort(d)}: ${count}/${total} available`} />
                      <span className={`text-xs font-medium ${isToday ? "text-amber-600 font-bold" : isWeekend ? "text-gray-300" : "text-gray-400"}`} style={{ fontSize: "9px" }}>{d.getDate()}</span>
                      <span className="text-gray-400" style={{ fontSize: "8px" }}>{getDayLabel(d).slice(0, 2)}</span>
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center gap-4 mt-2 pt-3 border-t border-gray-50">
                {[["#10b981","High (≥70%)"],["#f59e0b","Medium (40–69%)"],["#ef4444","Low (<40%)"],["#e5e7eb","Weekend/Holiday"]].map(([c, l]) => (
                  <span key={l} className="flex items-center gap-1.5 text-xs text-gray-500"><span className="w-3 h-3 rounded inline-block" style={{ background: c }} />{l}</span>
                ))}
              </div>
            </div>

            {/* Employee availability score cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {filteredEmps.sort((a, b) => (availScore[String(b.id)] ?? 0) - (availScore[String(a.id)] ?? 0)).map(emp => {
                const key = String(emp.id);
                const score = availScore[key] ?? 0;
                const leaveDates = empLeaveDates[key] || new Set<string>();
                const assignedDates = empAssignedDates[key] || new Set<string>();
                const dc = DEPT_COLORS[emp.department] || { bg: "#f3f4f6", text: "#374151", bar: "#6b7280" };
                const todayStatus = getCellStatus(toYMD(today), 0, leaveDates, holidayDates, assignedDates);
                const leaveDaysInRange = dateColumns.filter(d => leaveDates.has(toYMD(d))).length;
                const assignedDaysInRange = dateColumns.filter(d => assignedDates.has(toYMD(d))).length;
                const workDays = dateColumns.filter(d => { const dow = d.getDay(); return dow !== 0 && dow !== 6; }).length;
                const availDays = workDays - leaveDaysInRange - assignedDaysInRange;

                return (
                  <div key={emp.id} className="bg-white rounded-xl border border-gray-100 p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full bg-amber-500 flex items-center justify-center text-white font-bold flex-shrink-0">{emp.avatar}</div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-gray-900 truncate">{emp.name}</p>
                        <span className="text-xs px-1.5 py-0.5 rounded-full font-medium" style={{ background: dc.bg, color: dc.text }}>{emp.department}</span>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className={`text-lg font-black ${score >= 70 ? "text-amber-600" : score >= 40 ? "text-amber-500" : "text-red-500"}`}>{score}%</p>
                        <p className="text-xs text-gray-400">available</p>
                      </div>
                    </div>

                    {/* Score bar */}
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden mb-3">
                      <div className="h-full rounded-full transition-all" style={{ width: `${score}%`, background: score >= 70 ? "#10b981" : score >= 40 ? "#f59e0b" : "#ef4444" }} />
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-3 gap-2 mb-3">
                      {[
                        { label: "Available", value: availDays, color: "#10b981", bg: "#ecfdf5" },
                        { label: "On Leave", value: leaveDaysInRange, color: "#ef4444", bg: "#fef2f2" },
                        { label: "Assigned", value: assignedDaysInRange, color: "#f59e0b", bg: "#fffbeb" },
                      ].map(s => (
                        <div key={s.label} className="rounded-lg p-2 text-center" style={{ background: s.bg }}>
                          <p className="text-sm font-bold" style={{ color: s.color }}>{s.value}</p>
                          <p className="text-xs" style={{ color: s.color, opacity: 0.8 }}>{s.label}</p>
                        </div>
                      ))}
                    </div>

                    {/* Today status */}
                    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium ${
                      todayStatus === "Available" ? "bg-amber-50 text-amber-700" :
                      todayStatus === "On Leave" ? "bg-red-50 text-red-600" :
                      todayStatus === "Assigned" ? "bg-amber-50 text-amber-700" :
                      "bg-gray-50 text-gray-500"
                    }`}>
                      <i className={`${todayStatus === "Available" ? "ri-user-follow-line" : todayStatus === "On Leave" ? "ri-user-unfollow-line" : todayStatus === "Assigned" ? "ri-map-pin-user-line" : "ri-calendar-line"} text-sm`} />
                      Today: {todayStatus}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── WEEKLY SUMMARY TABLE ── */}
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <h3 className="text-sm font-semibold text-gray-800 mb-4">Department Availability Summary</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {["Field Operations","Office","Management","Lab"].map(dept => {
              const deptEmps = allStaff.filter(e => e.department === dept);
              const dc = DEPT_COLORS[dept] || { bg: "#f3f4f6", text: "#374151", bar: "#6b7280" };
              const avgScore = deptEmps.length > 0 ? Math.round(deptEmps.reduce((sum, e) => sum + (availScore[String(e.id)] ?? 0), 0) / deptEmps.length) : 0;
              const availToday = deptEmps.filter(emp => {
                const key = String(emp.id);
                const s = getCellStatus(toYMD(today), 0, empLeaveDates[key] || new Set(), holidayDates, empAssignedDates[key] || new Set());
                return s === "Available";
              }).length;
              return (
                <div key={dept} className="rounded-xl border border-gray-100 p-4" style={{ background: dc.bg + "60" }}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-bold" style={{ color: dc.text }}>{dept}</span>
                    <span className="text-xs text-gray-500">{deptEmps.length} staff</span>
                  </div>
                  <p className="text-2xl font-black mb-1" style={{ color: dc.bar }}>{avgScore}%</p>
                  <p className="text-xs text-gray-500 mb-2">avg availability ({daysToShow}d)</p>
                  <div className="h-1.5 bg-white/60 rounded-full overflow-hidden mb-2">
                    <div className="h-full rounded-full" style={{ width: `${avgScore}%`, background: dc.bar }} />
                  </div>
                  <p className="text-xs font-medium" style={{ color: dc.text }}>{availToday}/{deptEmps.length} available today</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── FIND AVAILABLE DATES MODAL ── */}
      {showPlanModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div>
                <h3 className="font-semibold text-gray-900">Find Available Dates</h3>
                <p className="text-xs text-gray-400 mt-0.5">Find days when enough team members are free</p>
              </div>
              <button onClick={() => { setShowPlanModal(false); setPlanResult(null); }} className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 cursor-pointer"><i className="ri-close-line text-lg" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Project / Job Title</label>
                <input type="text" placeholder="e.g. Topographic Survey — Shamshabad" value={planForm.jobTitle} onChange={e => setPlanForm({ ...planForm, jobTitle: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Search From <span className="text-red-400">*</span></label>
                  <input type="date" value={planForm.from} onChange={e => setPlanForm({ ...planForm, from: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 cursor-pointer" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Search To <span className="text-red-400">*</span></label>
                  <input type="date" value={planForm.to} min={planForm.from} onChange={e => setPlanForm({ ...planForm, to: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 cursor-pointer" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Department</label>
                  <select value={planForm.dept} onChange={e => setPlanForm({ ...planForm, dept: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 cursor-pointer">
                    {departments.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Min. Staff Needed</label>
                  <select value={planForm.minAvailable} onChange={e => setPlanForm({ ...planForm, minAvailable: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 cursor-pointer">
                    {[1,2,3,4,5].map(n => <option key={n} value={String(n)}>{n} person{n > 1 ? "s" : ""}</option>)}
                  </select>
                </div>
              </div>
              <button onClick={handleFindDates} disabled={!planForm.from || !planForm.to} className="w-full py-2.5 bg-amber-500 text-white rounded-xl text-sm font-semibold hover:bg-amber-600 cursor-pointer whitespace-nowrap disabled:opacity-50">
                <i className="ri-search-line mr-2" />Find Available Dates
              </button>

              {/* Results */}
              {planResult !== null && (
                <div className="border-t border-gray-100 pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-semibold text-gray-800">Results</h4>
                    <span className="text-xs text-gray-400">{planResult.length} suitable day{planResult.length !== 1 ? "s" : ""} found</span>
                  </div>
                  {planResult.length === 0 ? (
                    <div className="text-center py-6 bg-red-50 rounded-xl border border-red-100">
                      <i className="ri-calendar-close-line text-3xl text-red-300 block mb-2" />
                      <p className="text-sm font-medium text-red-600">No suitable dates found</p>
                      <p className="text-xs text-red-400 mt-1">Try reducing the minimum staff requirement or extending the date range</p>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-56 overflow-y-auto">
                      {planResult.map(r => (
                        <div key={r.date} className="flex items-center gap-3 p-3 bg-amber-50 rounded-xl border border-amber-100">
                          <div className="w-10 h-10 rounded-xl bg-amber-100 flex flex-col items-center justify-center flex-shrink-0">
                            <span className="text-xs font-bold text-amber-600 leading-none">{new Date(r.date + "T00:00:00").toLocaleDateString("en-IN", { month: "short" }).toUpperCase()}</span>
                            <span className="text-base font-black text-amber-700 leading-tight">{new Date(r.date + "T00:00:00").getDate()}</span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold text-gray-800">{new Date(r.date + "T00:00:00").toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" })}</p>
                            <p className="text-xs text-amber-600 mt-0.5 truncate">{r.available.slice(0, 3).join(", ")}{r.available.length > 3 ? ` +${r.available.length - 3} more` : ""}</p>
                          </div>
                          <span className="text-xs font-bold text-amber-700 bg-amber-100 px-2 py-1 rounded-lg flex-shrink-0">{r.available.length} free</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </ERPLayout>
  );
}
